import type { SvgIconComponent } from '@mui/icons-material';
import type { RouteObject } from 'react-router-dom';
import type { NavGate, NavSection, NavViewer } from '@/components/layout/navigation';

/**
 * The modules the console is made of. One key per manifest file in this
 * folder; the registry lists them in display order.
 */
export type ModuleKey =
  | 'clinic'
  | 'pharmacy'
  | 'logistics'
  | 'fleet'
  | 'directory'
  | 'reference'
  | 'access'
  | 'myOrganization'
  | 'marketplace'
  | 'financing';

/**
 * Where a module offers itself.
 *
 * `grid` is a tile on `/modules`. `organizationMenu` is an entry in the top
 * bar's organization menu instead — "My organization" is the tenant's own
 * record, one page, the same category as Profile and Settings, so it is not a
 * tile (owner's call, 2026-09-27). It is still a manifest, gated and guarded
 * like any module, so promoting it to a real tile later is this one field.
 */
export type ModuleSurface = 'grid' | 'organizationMenu';

interface ModuleCommon extends NavGate {
  key: ModuleKey;
  titleKey: string;
  descriptionKey: string;
  icon: SvgIconComponent;
}

export interface ModuleManifest extends ModuleCommon {
  /**
   * The URL prefix that owns this module. `activeModule()` derives the visible
   * sidebar from the location by longest prefix match, so a deep link, a
   * refresh and the back button all restore the same sidebar.
   */
  prefix: string;
  /** The module's own sidebar. Empty means the module's pages are full-width. */
  sections: NavSection[];
  /** Where the tile (or the landing redirect) sends this viewer. */
  landing: (viewer: NavViewer) => string;
  /**
   * The module's pages, inside the console shell. The route table wraps these
   * in `RequireModule` for this manifest — that wrapping is generated from the
   * registry, so it cannot be forgotten for a new module.
   */
  routes: RouteObject[];
  /**
   * Pages that must render *outside* the shell: a printed sheet is the whole
   * page, so the browser prints paper and not a sidebar. Guarded identically.
   */
  printRoutes?: RouteObject[];
  /** Defaults to `'grid'`. */
  surface?: ModuleSurface;
  comingSoon?: false;
}

/** On the roadmap: a tile, disabled, with a badge — never a dead route. */
export interface PlannedModule extends ModuleCommon {
  comingSoon: true;
}

export type RegistryEntry = ModuleManifest | PlannedModule;

export function isBuilt(entry: RegistryEntry): entry is ModuleManifest {
  return entry.comingSoon !== true;
}

/** A tile on the apps grid, as opposed to a top-bar entry. */
export function isGridModule(entry: RegistryEntry): boolean {
  return !isBuilt(entry) || (entry.surface ?? 'grid') === 'grid';
}
